// Slice 10 — public HR self-serve signup (DESIGN §6.6). Tenant entry point;
// no auth required, no role gate. The server-side anti-abuse stack (rate limit
// + disposable-email block + Turnstile) does the load-bearing protection.
//
// The Turnstile widget is intentionally absent here — slice 10 ships the
// server-side validation hook + env var (`TURNSTILE_SECRET`), and the widget
// integration on the page lands when production credentials are issued.

import { useState, type FormEvent } from 'react';
import { redirect } from 'react-router';
import {
  DPA_PLACEHOLDER_TEXT,
  DPA_PLACEHOLDER_VERSION,
  HEADCOUNT_BANDS,
  type HeadcountBand,
  SignupRequestSchema,
  type SignupRequest,
} from '@besideu/shared/signup';
import { Logo } from '@/components/brand/Logo.tsx';
import { api, ApiError } from '@/lib/api.ts';
import { fetchMe, landingPathFor } from '@/lib/auth.ts';
import { Button } from '@/components/ui/Button.tsx';
import { Field, TextInput, Select } from '@/components/ui/Field.tsx';
import { DpaAndSafetyAcceptance } from '@/components/DpaAndSafetyAcceptance.tsx';

export async function signUpLoader() {
  const session = await fetchMe();
  // eslint-disable-next-line @typescript-eslint/only-throw-error
  if (session) throw redirect(landingPathFor(session.role));
  return null;
}

export function SignUp() {
  const [organizationName, setOrganizationName] = useState('');
  const [adminName, setAdminName] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  // PROJECT §4 Step 1 — headcount band + two named contacts.
  const [headcountBand, setHeadcountBand] = useState<HeadcountBand | ''>('');
  const [primaryContactName, setPrimaryContactName] = useState('');
  const [primaryContactEmail, setPrimaryContactEmail] = useState('');
  const [escalationContactName, setEscalationContactName] = useState('');
  const [escalationContactEmail, setEscalationContactEmail] = useState('');
  const [dpaAccepted, setDpaAccepted] = useState(false);
  const [safetyAck, setSafetyAck] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<string, string>>>({});
  const [pending, setPending] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    if (!headcountBand) {
      setFieldErrors({ headcountBand: 'Choose a headcount band.' });
      return;
    }

    const payload: SignupRequest = {
      organizationName,
      adminName,
      adminEmail,
      headcountBand,
      primaryContact: { name: primaryContactName, email: primaryContactEmail },
      escalationContact: { name: escalationContactName, email: escalationContactEmail },
      dpaVersionAccepted: DPA_PLACEHOLDER_VERSION,
      hrSafetyContactAcknowledged: safetyAck === true ? true : (false as never),
    };
    const parsed = SignupRequestSchema.safeParse(payload);
    if (!parsed.success) {
      const next: Partial<Record<string, string>> = {};
      for (const issue of parsed.error.issues) {
        const key = String(issue.path[0]);
        if (!next[key]) next[key] = issue.message;
      }
      setFieldErrors(next);
      return;
    }
    if (!dpaAccepted) {
      setError('Please accept the placeholder DPA to continue.');
      return;
    }
    setFieldErrors({});
    setPending(true);
    try {
      await api.post('/api/organizations', parsed.data);
      setSubmitted(true);
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 429) {
          setError('Too many sign-up attempts. Try again in an hour.');
        } else if (err.status === 400 && err.message === 'email_rejected') {
          setError('That email address can’t be used to sign up. Try a work email.');
        } else if (err.status === 400 && err.message === 'captcha_failed') {
          setError('Captcha verification failed. Refresh and try again.');
        } else if (err.status === 400) {
          setError('Please check the form and try again.');
        } else {
          setError('Something went wrong. Try again in a moment.');
        }
      } else {
        setError('Network error.');
      }
    } finally {
      setPending(false);
    }
  }

  if (submitted) {
    return (
      <main className="mx-auto flex min-h-dvh max-w-md flex-col justify-center px-6 py-12">
        <Logo className="text-2xl" />
        <h1 className="mt-6 text-2xl font-semibold tracking-tight">Check your inbox</h1>
        <p className="mt-3 text-sm text-muted-foreground">
          If your address is eligible to sign up, a verification link is on its way. Click it to
          activate your organisation. The link expires in 15 minutes.
        </p>
      </main>
    );
  }

  return (
    <main className="mx-auto flex min-h-dvh max-w-2xl flex-col justify-center px-6 py-12">
      <Logo className="text-2xl" />
      <h1 className="mt-6 text-2xl font-semibold tracking-tight">
        Create your besideU organisation
      </h1>
      <p className="mt-2 text-sm text-muted-foreground">
        Self-serve sign-up for the first HR Admin. Managers and Employees are invited from the HR
        shell after you&rsquo;ve verified your email.
      </p>

      <form onSubmit={onSubmit} className="mt-8 grid gap-5">
        <Field label="Organisation name" error={fieldErrors.organizationName}>
          {(id, descProps) => (
            <TextInput
              id={id}
              {...descProps}
              required
              autoComplete="organization"
              value={organizationName}
              onChange={(e) => setOrganizationName(e.target.value)}
            />
          )}
        </Field>

        <Field
          label="Work email"
          error={fieldErrors.adminEmail}
          hint="Use a work address; we don’t accept disposable email domains."
        >
          {(id, descProps) => (
            <TextInput
              id={id}
              {...descProps}
              type="email"
              required
              autoComplete="email"
              value={adminEmail}
              onChange={(e) => setAdminEmail(e.target.value)}
            />
          )}
        </Field>

        {/* PROJECT §4 Step 1 — headcount band. Renders below the admin's own
            email so the operator doesn't have to scroll past company-level
            fields to enter their personal details. */}
        <Field
          label="Organisation size"
          error={fieldErrors.headcountBand}
          hint="Banded so we can tune Maeve and aggregate metrics to your team scale."
        >
          {(id, descProps) => (
            <Select
              id={id}
              {...descProps}
              required
              value={headcountBand}
              onChange={(e) => setHeadcountBand(e.target.value as HeadcountBand | '')}
            >
              <option value="" disabled>
                Choose a band…
              </option>
              {HEADCOUNT_BANDS.map((b) => (
                <option key={b} value={b}>
                  {b}
                </option>
              ))}
            </Select>
          )}
        </Field>

        <fieldset className="grid gap-4 rounded-lg border border-border p-4">
          <legend className="px-1 text-sm font-medium text-foreground">Primary contact</legend>
          <p className="-mt-2 text-xs text-muted-foreground">
            Day-to-day HR lead Maeve and the besideU shell reach for case escalations and red-flag
            notifications.
          </p>
          <Field label="Name" error={fieldErrors['primaryContact.name']}>
            {(id, descProps) => (
              <TextInput
                id={id}
                {...descProps}
                required
                autoComplete="name"
                value={primaryContactName}
                onChange={(e) => setPrimaryContactName(e.target.value)}
              />
            )}
          </Field>
          <Field label="Work email" error={fieldErrors['primaryContact.email']}>
            {(id, descProps) => (
              <TextInput
                id={id}
                {...descProps}
                type="email"
                required
                autoComplete="email"
                value={primaryContactEmail}
                onChange={(e) => setPrimaryContactEmail(e.target.value)}
              />
            )}
          </Field>
        </fieldset>

        <fieldset className="grid gap-4 rounded-lg border border-border p-4">
          <legend className="px-1 text-sm font-medium text-foreground">Escalation contact</legend>
          <p className="-mt-2 text-xs text-muted-foreground">
            Senior approver reached when the primary contact is unreachable.
          </p>
          <Field label="Name" error={fieldErrors['escalationContact.name']}>
            {(id, descProps) => (
              <TextInput
                id={id}
                {...descProps}
                required
                autoComplete="name"
                value={escalationContactName}
                onChange={(e) => setEscalationContactName(e.target.value)}
              />
            )}
          </Field>
          <Field label="Work email" error={fieldErrors['escalationContact.email']}>
            {(id, descProps) => (
              <TextInput
                id={id}
                {...descProps}
                type="email"
                required
                autoComplete="email"
                value={escalationContactEmail}
                onChange={(e) => setEscalationContactEmail(e.target.value)}
              />
            )}
          </Field>
        </fieldset>

        <Field label="Your name" error={fieldErrors.adminName}>
          {(id, descProps) => (
            <TextInput
              id={id}
              {...descProps}
              required
              autoComplete="name"
              value={adminName}
              onChange={(e) => setAdminName(e.target.value)}
            />
          )}
        </Field>

        <DpaAndSafetyAcceptance
          dpaSource="constant"
          dpaVersion={DPA_PLACEHOLDER_VERSION}
          dpaBody={DPA_PLACEHOLDER_TEXT}
          dpaAccepted={dpaAccepted}
          onDpaAcceptedChange={setDpaAccepted}
          safetyAck={safetyAck}
          onSafetyAckChange={setSafetyAck}
          required
        />

        <Button type="submit" loading={pending} disabled={!dpaAccepted || !safetyAck}>
          Create organisation
        </Button>
        {error ? (
          <p className="text-sm text-traffic-red" role="alert">
            {error}
          </p>
        ) : null}
      </form>
    </main>
  );
}
